/**
 * Alert sound resolution for 5.0 campuses (CloudAlertsPage).
 *
 * Which sound an alert plays is configured in the database
 * (sp_getPalCareSounds → session `sounds`) as a manufacture_id → device_type_id →
 * file-name map. This module turns that file name into a bundled asset URL.
 *
 * The whole per-device scheme is opt-in per campus. `custom_sounds` on the campus
 * alert settings gates it: when it is off, every alert plays LEGACY_FILE, the
 * single tone campuses used before per-device sounds existed. A community that
 * has not opted in hears exactly what it heard before, including for high
 * priority alerts.
 *
 * Resolution order for an alarm:
 *   0. custom_sounds off — LEGACY_FILE, no exceptions.
 *   1. High priority wins outright — an alert flagged high priority (the same flag
 *      that drives the banner and pinning) always plays HIGH_PRIORITY_FILE,
 *      whatever its manufacturer or device type.
 *   2. Otherwise the configured file for (manufacture_id, device_type_id).
 *   3. Otherwise DEFAULT_FILE — also used when a configured name has no matching
 *      file in this directory, so a bad/renamed config row degrades to a sound
 *      rather than to silence.
 *
 * Files are discovered with import.meta.glob rather than listed here, so dropping
 * a new .wav into src/sound/ makes it usable immediately — the database stays the
 * only place sounds are configured.
 */

export const DEFAULT_FILE = 'default_alert.wav';
export const HIGH_PRIORITY_FILE = 'high_priority_alert.wav';
/** The pre-per-device tone. Played for everything when custom_sounds is off. */
export const LEGACY_FILE = 'emergency_tone.mp3';

/**
 * Is the per-device sound scheme enabled for this campus?
 *
 * `custom_sounds` is a MySQL tinyint, so it can arrive as 1/0, as true/false once
 * something has coerced it, or as the strings "1"/"0" after a JSON round trip.
 * Boolean("0") is true, so the string case has to go through Number() rather than
 * a bare truthiness test. Anything unrecognised reads as off, which keeps a
 * campus that predates the column on the tone it already had.
 *
 * @param {number|string|boolean|null|undefined} value
 * @returns {boolean}
 */
export function customSoundsOn(value) {
  if (value === true) return true;
  if (value === false || value == null || value === '') return false;
  const n = Number(value);
  return Number.isFinite(n) && n !== 0;
}

/**
 * basename → hashed bundle URL for everything in this directory.
 * Eager so lookups are synchronous at play time (no dynamic import latency when
 * an alarm lands), and query:'?url' so Vite emits the file and hands back its URL.
 */
const urlsByPath = import.meta.glob('./*.{wav,mp3,ogg}', {
  eager: true,
  query: '?url',
  import: 'default',
});

const urlsByFile = Object.freeze(
  Object.fromEntries(
    Object.entries(urlsByPath).map(([path, url]) => [path.replace(/^\.\//, ''), url]),
  ),
);

/** Every sound file bundled from this directory, for diagnostics/tests. */
export function availableSoundFiles() {
  return Object.keys(urlsByFile);
}

/** Bundle URL for a bare file name, or null when no such file is bundled. */
export function soundUrlFor(fileName) {
  if (!fileName) return null;
  return urlsByFile[String(fileName).trim()] || null;
}

/**
 * The configured file name for a device, ignoring priority.
 *
 * `sounds` arrives from JSON, so its keys are strings even though the procedure
 * selects integers — coerce both levels or every lookup misses.
 *
 * @param {object} sounds — session `sounds` map
 * @param {number|string} manufactureId
 * @param {number|string} deviceTypeId
 * @returns {string|null}
 */
export function configuredSoundFile(sounds, manufactureId, deviceTypeId) {
  if (!sounds || manufactureId == null || deviceTypeId == null) return null;
  const byType = sounds[String(manufactureId)];
  if (!byType) return null;
  return byType[String(deviceTypeId)] || null;
}

/**
 * Resolve the sound for one alarm.
 *
 * @param {object} args
 * @param {object} args.sounds — session `sounds` map
 * @param {object} args.alarm — alert row (needs manufacture_id, device_type_id)
 * @param {boolean} args.isHighPriority — result of the page's high-priority test
 * @param {number|string|boolean} args.customSounds — campus `custom_sounds` flag,
 *   raw as it arrives. Defaults to off so a caller that forgets it gets the
 *   legacy tone rather than silently opting a campus in.
 * @returns {{ file: string, url: string|null, reason: string }} reason is for
 *   debugging/tests: 'legacy' | 'high-priority' | 'configured' | 'unmapped' |
 *   'missing-file'
 */
export function resolveAlarmSound({ sounds, alarm, isHighPriority = false, customSounds = false }) {
  // 0. Campus has not opted in: one tone for everything, high priority included.
  if (!customSoundsOn(customSounds)) {
    const legacyUrl = soundUrlFor(LEGACY_FILE);
    if (legacyUrl) return { file: LEGACY_FILE, url: legacyUrl, reason: 'legacy' };
    return { file: DEFAULT_FILE, url: soundUrlFor(DEFAULT_FILE), reason: 'missing-file' };
  }

  // 1. High priority overrides the device mapping entirely.
  if (isHighPriority) {
    const hpUrl = soundUrlFor(HIGH_PRIORITY_FILE);
    if (hpUrl) return { file: HIGH_PRIORITY_FILE, url: hpUrl, reason: 'high-priority' };
    // The high-priority file itself is missing — fall through to the default
    // rather than going silent on the alerts that matter most.
    return { file: DEFAULT_FILE, url: soundUrlFor(DEFAULT_FILE), reason: 'missing-file' };
  }

  // 2. Configured mapping for this manufacturer + device type.
  const configured = configuredSoundFile(sounds, alarm?.manufacture_id, alarm?.device_type_id);
  if (configured) {
    const url = soundUrlFor(configured);
    if (url) return { file: configured, url, reason: 'configured' };
    return { file: DEFAULT_FILE, url: soundUrlFor(DEFAULT_FILE), reason: 'missing-file' };
  }

  // 3. Nothing configured for this device.
  return { file: DEFAULT_FILE, url: soundUrlFor(DEFAULT_FILE), reason: 'unmapped' };
}

/**
 * Choose which alarm's sound to play for a batch of newly-seen alarms.
 *
 * Only one sound plays per batch (unchanged from the single-tone behaviour), so
 * when a batch maps to more than one sound there has to be a winner. The
 * highest-priority new alarm takes it; equal priority falls back to the first,
 * which keeps the choice from depending on subscription arrival order.
 *
 * @param {Array<object>} alarms — newly-seen alarms, in list order
 * @param {(alarm: object) => boolean} isHighPriority
 * @returns {{ alarm: object, isHighPriority: boolean } | null} null for an empty batch
 */
export function pickAlarmForSound(alarms, isHighPriority = () => false) {
  let chosen = null;
  let chosenHigh = false;
  (alarms || []).forEach((alarm) => {
    const high = Boolean(isHighPriority(alarm));
    // First alarm claims it; a later one only displaces an incumbent that isn't
    // already high priority.
    if (!chosen || (high && !chosenHigh)) {
      chosen = alarm;
      chosenHigh = high;
    }
  });
  return chosen ? { alarm: chosen, isHighPriority: chosenHigh } : null;
}

/**
 * Play a resolved sound URL.
 *
 * Audio elements are cached per URL and rewound rather than recreated, so a busy
 * community doesn't accumulate elements. Playback still requires a prior user
 * gesture on the page — same browser autoplay constraint the previous useSound
 * implementation had.
 */
const players = new Map();

export function playSoundUrl(url, volume = 0.5) {
  if (!url) return;
  try {
    let audio = players.get(url);
    if (!audio) {
      audio = new Audio(url);
      players.set(url, audio);
    }
    audio.volume = volume;
    audio.currentTime = 0;
    const p = audio.play();
    // Autoplay rejection is expected before the first user gesture; swallow it so
    // it doesn't surface as an unhandled rejection.
    if (p && typeof p.catch === 'function') p.catch(() => {});
  } catch {
    /* audio unavailable — never let a sound failure break alert rendering */
  }
}
